/**
 * Browser-relayed ingestion.
 *
 * Some municipal video hosts put their media behind a CDN WAF that rejects
 * datacenter traffic, so our servers get a 403 while an ordinary connection
 * gets a 200. The operator's browser fetches the audio and streams it to us
 * — the same request from the same machine the operator would use to
 * download it by hand, just without the manual step.
 *
 * Only audio moves. The video can be gigabytes; the audio rendition is tens
 * of megabytes, and nothing downstream needs the pictures.
 */
import { api } from "./api";
import { extractAdts, planAudioExtraction, type RangeReader } from "./mp4audio";

export interface RelayProgress {
  phase: "discovering" | "transferring" | "finishing";
  bytesSent: number;
  totalBytes: number | null;
  partNumber: number;
  /**
   * Set when the station's audio track is cut short and the sound is being
   * read out of the full video file instead -- far slower, so the operator
   * should know why, and to leave the tab open.
   */
  fromVideo?: { audioMinutes: number; videoMinutes: number };
}

interface StartResponse {
  key: string;
  uploadId: string;
  partBytes: number;
  mediaBaseUrl: string | null;
}

/** Resolve a possibly-relative HLS URI against the playlist directory. */
function absolute(base: string, uri: string): string {
  if (/^https?:\/\//i.test(uri)) return uri;
  return `${base.replace(/\/$/, "")}/${uri.replace(/^\.?\//, "")}`;
}

async function text(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  }
}

/**
 * Find the audio-only rendition.
 *
 * The master playlist lists audio as an EXT-X-MEDIA entry; that playlist in
 * turn maps to a single fMP4 addressed by byte ranges, which means the whole
 * file can simply be fetched. Falls back to conventional filenames.
 */
export async function discoverAudioUrl(base: string): Promise<string> {
  const master = await text(`${base}/vod.m3u8`);
  if (master) {
    const media = master.match(/#EXT-X-MEDIA:[^\n]*TYPE=AUDIO[^\n]*URI="([^"]+)"/i);
    if (media?.[1]) {
      const playlistUrl = absolute(base, media[1]);
      const playlist = await text(playlistUrl);
      if (playlist) {
        const dir = playlistUrl.replace(/\/[^/]+$/, "");
        const map = playlist.match(/#EXT-X-MAP:URI="([^"]+)"/i);
        if (map?.[1]) return absolute(dir, map[1]);
        const seg = playlist.split(/\r?\n/).find((l) => l && !l.startsWith("#"));
        if (seg) return absolute(dir, seg);
      }
    }
  }
  for (const guess of ["1080p_audio.mp4", "720p_audio.mp4", "audio.mp4"]) {
    try {
      const res = await fetch(`${base}/${guess}`, { headers: { Range: "bytes=0-1" } });
      if (res.ok || res.status === 206) return `${base}/${guess}`;
    } catch { /* try the next */ }
  }
  throw new Error("Could not find an audio track for this recording.");
}

/** Total length of a media playlist, from its segment durations. */
async function playlistSeconds(url: string): Promise<number | null> {
  const body = await text(url);
  if (!body) return null;
  const secs = [...body.matchAll(/#EXTINF:([\d.]+)/g)].reduce((n, m) => n + Number(m[1]), 0);
  return secs > 0 ? secs : null;
}

/**
 * How long the audio rendition and the picture run. A station's encoder can
 * cut the audio track short while the video is whole; relaying that track
 * would transcribe the first part of the meeting and silently lose the rest.
 */
export async function renditionLengths(base: string): Promise<{ audioS: number | null; videoS: number | null }> {
  const master = await text(`${base}/vod.m3u8`);
  if (!master) return { audioS: null, videoS: null };
  const audio = master.match(/#EXT-X-MEDIA:[^\n]*TYPE=AUDIO[^\n]*URI="([^"]+)"/i)?.[1];
  const video = master.split(/\r?\n/).find((l, i, all) => i > 0 && all[i - 1]!.startsWith("#EXT-X-STREAM-INF") && l && !l.startsWith("#"));
  return {
    audioS: audio ? await playlistSeconds(absolute(base, audio)) : null,
    videoS: video ? await playlistSeconds(absolute(base, video)) : null,
  };
}

/** Uploads a stream of byte chunks as multipart parts of the server's size. */
async function uploadParts(
  id: string, start: StartResponse, source: AsyncIterable<Uint8Array>, totalBytes: number,
  onPart: (bytesSent: number, partNumber: number) => void,
): Promise<{ PartNumber: number; ETag: string }[]> {
  const parts: { PartNumber: number; ETag: string }[] = [];
  let buf = new Uint8Array(start.partBytes);
  let fill = 0;
  let sent = 0;
  const flush = async (last: boolean) => {
    if (fill === 0) return;
    const partNumber = parts.length + 1;
    const up = await fetch(
      `/api/meetings/${id}/relay/part?uploadId=${encodeURIComponent(start.uploadId)}` +
      `&partNumber=${partNumber}${last ? "&last=true" : ""}`,
      { method: "POST", credentials: "same-origin", headers: { "content-type": "application/octet-stream" }, body: buf.subarray(0, fill) },
    );
    if (!up.ok) {
      const j = await up.json().catch(() => ({}));
      throw new Error(j?.error === "stale_upload"
        ? "This fetch was restarted elsewhere, so this one stopped."
        : `Uploading part ${partNumber} failed (${j?.error ?? up.status}).`);
    }
    const { etag } = (await up.json()) as { etag: string };
    parts.push({ PartNumber: partNumber, ETag: etag });
    sent += fill;
    onPart(sent, partNumber);
    buf = new Uint8Array(start.partBytes);
    fill = 0;
  };
  for await (const chunk of source) {
    let p = 0;
    while (p < chunk.length) {
      const n = Math.min(chunk.length - p, buf.length - fill);
      buf.set(chunk.subarray(p, p + n), fill);
      fill += n; p += n;
      if (fill === buf.length) await flush(sent + fill >= totalBytes);
    }
  }
  await flush(true);
  return parts;
}

/**
 * The fallback when the audio rendition is short or missing: read the audio
 * out of the station's full video file, fetching only the byte ranges that
 * hold sound. Thousands of small requests, so it takes minutes, not seconds.
 */
async function relayFromVideo(
  id: string, base: string, lengths: { audioMinutes: number; videoMinutes: number },
  onProgress: (p: RelayProgress) => void,
): Promise<void> {
  const videoUrl = `${base}/vod.mp4`;
  const fromVideo = lengths;
  const head = await fetch(videoUrl, { method: "HEAD" }).catch(() => null);
  const size = Number(head?.headers.get("content-length"));
  if (!head?.ok || !Number.isFinite(size) || size <= 0) {
    throw new Error(
      `The station's audio track is only ${Math.round(lengths.audioMinutes)} of ${Math.round(lengths.videoMinutes)} minutes, ` +
      "and its full video file can't be read from here. Ask the station to re-publish the recording.");
  }
  const read: RangeReader = async (a, b) => {
    const res = await fetch(videoUrl, { headers: { Range: `bytes=${a}-${b}` } });
    if (res.status !== 206) throw new Error(`The video host returned ${res.status} while reading the audio.`);
    return new Uint8Array(await res.arrayBuffer());
  };
  const plan = await planAudioExtraction(read, size);

  const start = await api.post<StartResponse>(`/api/meetings/${id}/relay/start`, { format: "aac" });
  try {
    onProgress({ phase: "transferring", bytesSent: 0, totalBytes: plan.outputBytes, partNumber: 0, fromVideo });
    let fetched = 0;
    const parts = await uploadParts(
      id, start,
      extractAdts(read, plan, {
        // Browsers allow six connections to an HTTP/1.1 host.
        concurrency: 6,
        onProgress: (n) => {
          fetched = n;
          onProgress({ phase: "transferring", bytesSent: fetched, totalBytes: plan.outputBytes, partNumber: 0, fromVideo });
        },
      }),
      plan.outputBytes,
      () => {},
    );
    if (parts.length === 0) throw new Error("No audio data came back from the video host.");
    onProgress({ phase: "finishing", bytesSent: plan.outputBytes, totalBytes: plan.outputBytes, partNumber: parts.length, fromVideo });
    await api.post(`/api/meetings/${id}/relay/complete`, { uploadId: start.uploadId, parts, audioUrl: videoUrl });
  } catch (err) {
    await api.post(`/api/meetings/${id}/relay/abort`, { uploadId: start.uploadId }).catch(() => {});
    throw err;
  }
}

export async function relayAudio(
  meeting: { id: string; mediaBaseUrl: string | null },
  onProgress: (p: RelayProgress) => void,
): Promise<void> {
  const id = meeting.id;
  if (!meeting.mediaBaseUrl) {
    throw new Error("This meeting has no resolved media location. Re-resolve it first.");
  }

  // ---- Phase 1: prove the source is readable. No server state yet. ------
  // Anything that can fail here fails *before* an upload exists, so a
  // missing audio track or an unreadable host can never strand a meeting.
  onProgress({ phase: "discovering", bytesSent: 0, totalBytes: null, partNumber: 0 });

  // Is the audio track as long as the picture? If the station cut it short,
  // take the sound from the full video instead of relaying a partial meeting.
  const { audioS, videoS } = await renditionLengths(meeting.mediaBaseUrl);
  if (audioS !== null && videoS !== null && audioS < videoS * 0.95) {
    await relayFromVideo(id, meeting.mediaBaseUrl, { audioMinutes: audioS / 60, videoMinutes: videoS / 60 }, onProgress);
    return;
  }

  const audioUrl = await discoverAudioUrl(meeting.mediaBaseUrl);

  // Total size is for the progress bar only -- the loop below must not
  // depend on it. Content-Range is NOT a CORS-safelisted response header, so
  // cross-origin JavaScript reads it as null unless the host exposes it
  // (CCX does not). Content-Length is safelisted, so a HEAD gives the size.
  let totalBytes: number | null = null;
  try {
    const head = await fetch(audioUrl, { method: "HEAD" });
    const len = Number(head.headers.get("content-length"));
    if (head.ok && Number.isFinite(len) && len > 0) totalBytes = len;
  } catch { /* progress falls back to bytes-so-far */ }

  // ---- Phase 2: open the upload. From here, every failure must abort. ---
  const start = await api.post<StartResponse>(`/api/meetings/${id}/relay/start`);

  const parts: { PartNumber: number; ETag: string }[] = [];
  let offset = 0;
  let partNumber = 1;

  try {
    // Read range by range until a short chunk, an empty chunk or a 416.
    // Never assumes the total, so an unexposed header cannot stall it.
    for (;;) {
      const end = offset + start.partBytes - 1;
      const res = await fetch(audioUrl, { headers: { Range: `bytes=${offset}-${end}` } });

      // 416: the previous part ended exactly on the file boundary.
      if (res.status === 416) break;
      if (res.status === 200 && offset === 0) {
        // The host ignored Range and is sending the whole file at once,
        // which is too large to relay as a single part.
        throw new Error("The video host does not support partial downloads, so it can't be relayed.");
      }
      if (res.status !== 206 && !res.ok) {
        throw new Error(`The video host returned ${res.status} while reading the audio.`);
      }
      const chunk = await res.arrayBuffer();
      if (chunk.byteLength === 0) break;

      const isLast = totalBytes !== null
        ? offset + chunk.byteLength >= totalBytes
        : chunk.byteLength < start.partBytes;

      const up = await fetch(
        `/api/meetings/${id}/relay/part` +
        `?uploadId=${encodeURIComponent(start.uploadId)}` +
        `&partNumber=${partNumber}${isLast ? "&last=true" : ""}`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/octet-stream" },
          body: chunk,
        },
      );
      if (!up.ok) {
        const j = await up.json().catch(() => ({}));
        throw new Error(
          j?.error === "stale_upload"
            ? "This fetch was restarted elsewhere, so this one stopped."
            : `Uploading part ${partNumber} failed (${j?.error ?? up.status}).`,
        );
      }
      const { etag } = (await up.json()) as { etag: string };
      parts.push({ PartNumber: partNumber, ETag: etag });

      // Advance by what actually arrived, not by what was asked for.
      offset += chunk.byteLength;
      onProgress({ phase: "transferring", bytesSent: offset, totalBytes, partNumber });
      partNumber++;
      if (isLast) break;
    }

    if (parts.length === 0) throw new Error("No audio data came back from the video host.");
    onProgress({ phase: "finishing", bytesSent: offset, totalBytes: totalBytes ?? offset, partNumber });
    await api.post(`/api/meetings/${id}/relay/complete`, {
      uploadId: start.uploadId, parts, audioUrl,
    });
  } catch (err) {
    // Release the upload so the meeting returns to "needs browser fetch"
    // instead of sitting in "relaying" with nothing behind it.
    await api.post(`/api/meetings/${id}/relay/abort`, { uploadId: start.uploadId })
      .catch(() => {});
    throw err;
  }
}
